import { DialogTitle } from '@headlessui/react'
import { Listing } from '@tovala/browser-apis-combinedapi'
import { ReactNode } from 'react'

import { events } from 'analytics/events'
import { formatCentsToDollars } from 'utils/currency'
import { track } from 'utils/analytics'

import { ListingFull } from 'types/internal'
import {
  Allergens,
  ComponentImages,
  CookTime,
  DetailsCollapsible,
  ExpirationDate,
  MainImage,
  Nutrition,
  PreparationSteps,
  SectionID,
} from './shared'

export const ListingDetails = ({
  listing,
  stepper,
}: {
  listing: Listing | ListingFull
  stepper?: ReactNode
}) => {
  const onCollapsibleClick = (sectionID: SectionID) => {
    track(events.OPENS_EXTRA_DETAIL_DISCLOSURE_TAB, {
      extra_detail_section_id: sectionID,
    })
  }

  if ('subtitle' in listing) {
    return (
      <div className="flex-auto overflow-y-auto">
        <div className="mx-4 my-8">
          <DialogTitle className="text-k/28_110">{listing.title}</DialogTitle>
          <p className="text-k/24_120 text-grey-8">{listing.subtitle}</p>
        </div>

        <MainImage imageURL={listing.imageURL} stepper={stepper} />

        <div className="mx-4 mt-4">
          <p className="text-k/14_120">
            {formatCentsToDollars(listing.priceCents)}
          </p>
        </div>

        <div className="mx-4 my-4">
          <CookTime barcode={listing.barcode} />
        </div>

        <div className="mx-4 mb-16 mt-4">
          <h3 className="mb-4 text-k/20_110">Description</h3>
          <p className="mb-4 text-k/14_120 tracking-normal text-grey-9">
            {listing.story}
          </p>

          <ExpirationDate expirationDate={listing.expirationDate ?? ''} />

          <div className="divide-y divide-grey-4 border-b border-t border-grey-4">
            {listing.nutritionalInfo && (
              <div>
                <DetailsCollapsible
                  onCollapsibleClick={onCollapsibleClick}
                  sectionID="nutrition"
                  title="Nutrition"
                >
                  <Nutrition nutritionalInfo={listing.nutritionalInfo} />
                </DetailsCollapsible>
              </div>
            )}

            {listing.componentImages && (
              <div>
                <DetailsCollapsible
                  onCollapsibleClick={onCollapsibleClick}
                  sectionID="in_the_box"
                  title="In the Box"
                >
                  <div>
                    <ComponentImages images={listing.componentImages} />
                  </div>
                </DetailsCollapsible>
              </div>
            )}

            {listing.ingredients && (
              <div>
                <DetailsCollapsible
                  onCollapsibleClick={onCollapsibleClick}
                  sectionID="ingredients"
                  title="Ingredients"
                >
                  <div className="whitespace-pre-wrap text-k/14_120 capitalize text-grey-9">
                    {listing.ingredients}
                  </div>
                </DetailsCollapsible>
              </div>
            )}

            <div>
              <DetailsCollapsible
                onCollapsibleClick={onCollapsibleClick}
                sectionID="allergens"
                title="Allergens"
              >
                <div>
                  <Allergens allergens={listing.allergens} />
                </div>
              </DetailsCollapsible>
            </div>

            {listing.prepSteps && (
              <div>
                <DetailsCollapsible
                  onCollapsibleClick={onCollapsibleClick}
                  sectionID="prep_steps"
                  title="Preparation Steps"
                >
                  <PreparationSteps steps={listing.prepSteps} />
                </DetailsCollapsible>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Loading listing details
  return (
    <div className="flex-auto overflow-y-auto">
      <div className="mx-4 my-8">
        <DialogTitle className="text-k/28_110">{listing.title}</DialogTitle>
      </div>

      <MainImage imageURL={listing.imageURL} stepper={stepper} />

      <div className="mx-4 mt-8">
        <p className="text-k/14_120">
          {formatCentsToDollars(listing.priceCents)}
        </p>
      </div>
    </div>
  )
}
